import * as React from "react";
import { cn } from "../../lib/utils";
import { Checkbox } from "../ui/checkbox";

export interface MilestoneStep {
  id: string;
  label: string;
  description?: string;
  completed?: boolean;
}

export interface MilestoneSection {
  id: string;
  title: string;
  description: string;
  steps: MilestoneStep[];
}

export interface Milestone {
  id: string;
  label: string;
  sections: MilestoneSection[];
}

export interface MilestoneTrackerRef {
  reset: () => void;
  getCompletedSteps: () => string[];
  getCurrentMilestone: () => Milestone | undefined;
}

export interface MilestoneTrackerProps {
  milestones: Milestone[];
  onStepComplete?: (stepId: string, completed: boolean) => void;
  onMilestoneChange?: (milestoneId: string) => void;
  className?: string;
  storageKey?: string;
}

export interface MilestoneProgressProps {
  milestones: Milestone[];
  currentMilestoneIndex: number;
  className?: string;
}

export interface MilestoneSectionsProps {
  sections: MilestoneSection[];
  onStepToggle: (stepId: string) => void;
  completingStepId: string | null;
  className?: string;
}

// Progress Bar Component
export const MilestoneProgress = React.forwardRef<
  HTMLDivElement,
  MilestoneProgressProps
>(({ milestones, currentMilestoneIndex, className }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "rounded-xl border border-white/10 text-white shadow p-4 mb-6 flex-shrink-0",
        className
      )}
      style={{
        backgroundColor: "rgba(235, 220, 207, 0.045)",
        backdropFilter: "blur(1000px)",
        WebkitBackdropFilter: "blur(1000px)",
      }}
    >
      <div className="mb-4">
        <div className="text-sm font-semibold text-[#FAF1E9]">Progress</div>
      </div>
      <div className="relative overflow-x-auto scrollbar-hide -mx-4 px-4">
        <div className="flex items-start gap-6 relative pb-0 pt-4 w-max min-w-full">
          {milestones.map((milestone, index) => {
            const isCompleted = index < currentMilestoneIndex;
            const isActive = index === currentMilestoneIndex;
            const isLast = index === milestones.length - 1;

            return (
              <div
                key={milestone.id}
                className="flex flex-col items-center flex-shrink-0 relative min-w-[84px]"
              >
                {/* Connecting Line */}
                {!isLast && (
                  <div
                    className={cn(
                      "absolute top-[7px] left-[calc(50%+6px)] w-[calc(100%+24px)] h-0.5 transition-colors duration-700",
                      isCompleted ? "bg-[#EDC29C]" : "bg-white/10"
                    )}
                  />
                )}

                {/* Dot Circle */}
                <div className="relative flex items-center justify-center z-10">
                  <span
                    className={cn(
                      "w-4 h-4 rounded-full border-2 transition-all duration-300",
                      isCompleted
                        ? "bg-[#EDC29C] border-[#EDC29C]"
                        : isActive
                        ? "border-white/10 bg-[#161616]"
                        : "border-white/10 bg-[#161616]"
                    )}
                  />
                  {isActive && (
                    <span className="absolute h-6 w-6 rounded-full border-2 border-[#EDC29C]/25 animate-[slow-ping_2s_cubic-bezier(0,0,0.2,1)_infinite]" />
                  )}
                </div>

                {/* Label */}
                <div
                  className={cn(
                    "mt-3 text-xs text-center max-w-[84px] max-h-[60px] line-clamp-3 px-0 leading-tight",
                    isActive ? "text-[#EDC29C] font-semibold" : "text-white/60"
                  )}
                >
                  {milestone.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});
MilestoneProgress.displayName = "MilestoneProgress";

// Milestone Sections Component
export const MilestoneSections = React.forwardRef<
  HTMLDivElement,
  MilestoneSectionsProps
>(({ sections, onStepToggle, completingStepId, className }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "rounded-xl border border-white/10 text-white shadow p-4",
        className
      )}
      style={{
        backgroundColor: "rgba(235, 220, 207, 0.045)",
        backdropFilter: "blur(1000px)",
        WebkitBackdropFilter: "blur(1000px)",
      }}
    >
      {sections.map((section, sectionIndex) => (
        <React.Fragment key={section.id}>
          <div className="mb-8 last:mb-0">
            {/* Section Header */}
            <div className="text-xs font-bold uppercase tracking-wider mb-2 text-white/60">
              {section.title}
            </div>
            <div className="text-sm text-[#FAF1E9]/70 mb-4 leading-relaxed">
              {section.description}
            </div>

            {/* Steps */}
            <div className="flex flex-col gap-4">
              {section.steps.map((step) => {
                const isCompleting = completingStepId === step.id;
                const isCompleted = step.completed;

                return (
                  <div
                    key={step.id}
                    className="flex items-start gap-3 relative"
                  >
                    {/* Ripple effect */}
                    {isCompleting && (
                      <span className="absolute top-0 left-0 w-5 h-5 rounded-full bg-[#EDC29C]/30 animate-[ripple_0.8s_ease] pointer-events-none" />
                    )}

                    {/* Checkbox */}
                    <Checkbox
                      className="mt-0.5"
                      checked={isCompleted}
                      onCheckedChange={() => onStepToggle(step.id)}
                    />

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div
                        className={cn(
                          "text-sm font-semibold leading-tight transition-all duration-300 text-[#FAF1E9]",
                          isCompleted && "line-through opacity-60"
                        )}
                      >
                        {step.label}
                      </div>
                      {step.description && (
                        <div
                          className={cn(
                            "text-xs text-[#FAF1E9]/70 mt-1 leading-relaxed transition-all duration-300",
                            isCompleted && "line-through opacity-60"
                          )}
                        >
                          {step.description}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          {sectionIndex < sections.length - 1 && (
            <div className="h-px bg-white/10 my-8" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
});
MilestoneSections.displayName = "MilestoneSections";

// Main Milestone Tracker Component
export const MilestoneTracker = React.forwardRef<
  MilestoneTrackerRef,
  MilestoneTrackerProps
>(
  (
    {
      milestones,
      onStepComplete,
      onMilestoneChange,
      className,
      storageKey = "milestone-tracker-state",
    },
    ref
  ) => {
    const [completedSteps, setCompletedSteps] = React.useState<Set<string>>(
      () => {
        if (typeof window === "undefined") return new Set();
        try {
          const stored = localStorage.getItem(storageKey);
          return stored ? new Set(JSON.parse(stored)) : new Set();
        } catch {
          return new Set();
        }
      }
    );

    const [completingStepId, setCompletingStepId] = React.useState<
      string | null
    >(null);

    // Save to localStorage whenever completedSteps changes
    React.useEffect(() => {
      if (typeof window !== "undefined") {
        try {
          localStorage.setItem(
            storageKey,
            JSON.stringify(Array.from(completedSteps))
          );
        } catch (error) {
          console.error("Failed to save milestone state:", error);
        }
      }
    }, [completedSteps, storageKey]);

    // Calculate current milestone based on completed steps
    const currentMilestoneIndex = React.useMemo(() => {
      for (let i = 0; i < milestones.length; i++) {
        const milestone = milestones[i];
        const allSteps = milestone.sections.flatMap((s) => s.steps);
        const allCompleted = allSteps.every((step) =>
          completedSteps.has(step.id)
        );

        if (!allCompleted) {
          return i;
        }
      }
      return milestones.length - 1;
    }, [milestones, completedSteps]);

    // Notify parent of milestone changes
    React.useEffect(() => {
      if (milestones[currentMilestoneIndex]) {
        onMilestoneChange?.(milestones[currentMilestoneIndex].id);
      }
    }, [currentMilestoneIndex, milestones, onMilestoneChange]);

    // Get current milestone sections with completion status
    const currentSections = React.useMemo(() => {
      if (!milestones[currentMilestoneIndex]) return [];

      return milestones[currentMilestoneIndex].sections.map((section) => ({
        ...section,
        steps: section.steps.map((step) => ({
          ...step,
          completed: completedSteps.has(step.id),
        })),
      }));
    }, [milestones, currentMilestoneIndex, completedSteps]);

    const handleStepToggle = React.useCallback(
      (stepId: string) => {
        const isCurrentlyCompleted = completedSteps.has(stepId);
        const newCompleted = !isCurrentlyCompleted;

        if (newCompleted) {
          setCompletingStepId(stepId);
          setTimeout(() => setCompletingStepId(null), 800);
        }

        setCompletedSteps((prev) => {
          const next = new Set(prev);
          if (newCompleted) {
            next.add(stepId);
          } else {
            next.delete(stepId);
          }
          return next;
        });

        onStepComplete?.(stepId, newCompleted);
      },
      [completedSteps, onStepComplete]
    );

    // Reset function (can be exposed via ref)
    React.useImperativeHandle(ref, () => ({
      reset: () => {
        setCompletedSteps(new Set());
        if (typeof window !== "undefined") {
          localStorage.removeItem(storageKey);
        }
      },
      getCompletedSteps: () => Array.from(completedSteps),
      getCurrentMilestone: () => milestones[currentMilestoneIndex],
    }));

    return (
      <div className={cn("flex flex-col", className)}>
        <MilestoneProgress
          milestones={milestones}
          currentMilestoneIndex={currentMilestoneIndex}
        />
        <MilestoneSections
          sections={currentSections}
          onStepToggle={handleStepToggle}
          completingStepId={completingStepId}
        />
      </div>
    );
  }
);
MilestoneTracker.displayName = "MilestoneTracker";
